import { useRef, useState, useCallback } from 'react';
import { Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { Logo } from '../../logos/logo';
import { GiphySearch } from './giphy-search';
import { MMS_CONSTANTS, ERROR_MESSAGES } from '../utils/sms-constants';
import { upload } from '@vercel/blob/client';
import type { PutBlobResult } from '@vercel/blob';

const UPLOAD_ERROR = 'Failed to upload image. Please try again.';

// Default noop function for backward compatibility
const noopFileHandler = (_file: File) => {
  console.warn('No file handler provided to ImageUploadDialog');
};

// Extend PutBlobResult to include the original file
interface UploadResult extends PutBlobResult {
  file: File;
}

interface ImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // We'll keep both for now to avoid breaking existing code
  // Will remove onFileSelected in next PR
  onFileSelected?: (file: File) => void;
  onImageUploaded?: (result: UploadResult) => void;
}

export function ImageUploadDialog({
  open,
  onOpenChange,
  onFileSelected = noopFileHandler,
  onImageUploaded
}: ImageUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const validateFile = useCallback((file: File): boolean => {
    if (file.size > MMS_CONSTANTS.MAX_FILE_SIZE) {
      setError(ERROR_MESSAGES.FILE_TOO_LARGE);
      return false;
    }
    if (
      !MMS_CONSTANTS.SUPPORTED_FILE_TYPES.includes(
        file.type as (typeof MMS_CONSTANTS.SUPPORTED_FILE_TYPES)[number]
      )
    ) {
      setError(ERROR_MESSAGES.INVALID_FILE_TYPE);
      return false;
    }
    setError(null);
    return true;
  }, []);

  const handleFileUpload = useCallback(
    async (file: File) => {
      try {
        setIsUploading(true);
        setError(null);

        // For backward compatibility, call the old handler
        onFileSelected(file);

        if (onImageUploaded) {
          const blob = await upload(file.name, file, {
            access: 'public',
            handleUploadUrl: '/api/image/upload',
            clientPayload: JSON.stringify({ fileName: file.name })
          });
          onImageUploaded({ ...blob, file });
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(UPLOAD_ERROR);
      } finally {
        setIsUploading(false);
      }
    },
    [onFileSelected, onImageUploaded]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0] as File | undefined;
      if (!file) return;

      if (validateFile(file)) {
        handleFileUpload(file).catch((err: unknown) => {
          console.error('Failed to upload file:', err);
          setError(UPLOAD_ERROR);
        });
      }
    },
    [validateFile, handleFileUpload]
  );

  const handleImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      if (validateFile(file)) {
        handleFileUpload(file).catch((err: unknown) => {
          console.error('Failed to upload file:', err);
          setError(UPLOAD_ERROR);
        });
      }
    },
    [validateFile, handleFileUpload]
  );

  // Clear error when dialog closes
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setError(null);
        setIsUploading(false);
      }
      onOpenChange(open);
    },
    [onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add Image/GIF</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-lg">
            <TabsTrigger value="upload" className="rounded-lg">
              Upload
            </TabsTrigger>
            <TabsTrigger value="giphy" className="rounded-lg">
              <div className="flex items-center gap-2">
                <Logo icon="giphy" width={16} height={16} /> GIPHY
              </div>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="upload" className="focus-visible:ring-0 focus-visible:outline-none">
            <div
              className={cn(
                'flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg transition-colors focus:outline-none focus-visible:ring-0',
                isDragging
                  ? 'border-primary bg-muted'
                  : error
                    ? 'border-destructive bg-destructive/5'
                    : 'border-muted'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              tabIndex={-1}
            >
              <Upload
                className={cn('h-8 w-8 mb-4', error ? 'text-destructive' : 'text-muted-foreground')}
              />
              <p className="text-sm text-muted-foreground mb-2">
                {'Drop your image here to upload'}
              </p>
              <div className="space-y-1 text-xs text-muted-foreground mb-4 focus-visible:ring-0 focus-visible:outline-none">
                <p>Supports PNG, JPG, or GIF files under {MMS_CONSTANTS.MAX_FILE_SIZE_DISPLAY}</p>
                <p>• JPG format is recommended for best delivery</p>
                <p>• PNG files with transparency may display incorrectly</p>
              </div>
              {error && <p className="text-sm text-destructive mb-4 text-center">{error}</p>}
              <input
                ref={fileInputRef}
                type="file"
                accept={MMS_CONSTANTS.SUPPORTED_FILE_TYPES.join(',')}
                onChange={handleImageUpload}
                className="hidden"
                disabled={isUploading}
              />
              <Button
                variant="outline"
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                disabled={!!error || isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload from device'}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="giphy">
            <div className="p-6">
              <GiphySearch
                onGifSelected={(file, originalUrl) => {
                  // Call the legacy handler
                  onFileSelected(file);

                  // If we have the new handler, pass the original Giphy URL
                  if (onImageUploaded) {
                    onImageUploaded({
                      url: originalUrl,
                      downloadUrl: originalUrl,
                      pathname: originalUrl,
                      contentType: file.type,
                      contentDisposition: 'inline',
                      file
                    });
                  }
                }}
              />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
